import React from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ArrowBack, ArrowForward, Close, Check } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { useSiteImportUIStore } from '../../stores/siteImportUIStore';
import { useShellStore } from '../../../../shared/stores/shellStore';
import { SIDEBAR_WIDTH } from '../../../../shared/components/layout/sidebar/constants';
import { SiteImportFormData } from '../../schemas/siteImportSchema';
import { SiteImportPreviewResponse } from '../../api/sitesApi';
import { Routes } from '../../../../routes';

const stepLabels = [
  'Site Details',
  'Choose Source',
  'Choose Template',
  'Review & Confirm',
];

interface SiteImportBottomToolbarProps {
  previewData: SiteImportPreviewResponse | null;
  selectedTemplateId: string | null;
  allLabourItemsResolved: boolean;
  uploading: boolean;
  importing: boolean;
  onImport: () => void;
  onCancel: () => void;
  onTemplateConfirm: () => Promise<void>;
}

const SiteImportBottomToolbar: React.FC<SiteImportBottomToolbarProps> = ({
  previewData,
  selectedTemplateId,
  allLabourItemsResolved,
  uploading,
  importing,
  onImport,
  onCancel,
  onTemplateConfirm,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const form = useFormContext<SiteImportFormData>();
  const sidebarCollapsed = useShellStore(state => state.sidebarCollapsed);

  const {
    activeStep,
    previousStep,
    nextStep,
    importPath,
    setShowCancelConfirm,
  } = useSiteImportUIStore();

  const currentStepLabels = [...stepLabels];
  if (importPath === 'csv') {
    currentStepLabels[2] = 'Upload CSV';
  }

  const canGoNext = () => {
    switch (activeStep) {
      case 0: {
        const { errors } = form.formState;
        const values = form.getValues();
        return (
          !errors.name &&
          !errors.slug &&
          !errors.what3words &&
          (values.name?.length ?? 0) >= 3 &&
          (values.slug?.length ?? 0) === 4
        );
      }
      case 1:
        return importPath !== null;
      case 2:
        if (importPath === 'csv') {
          return Boolean(previewData);
        }
        return selectedTemplateId !== null;
      case 3: {
        const hasMissing =
          (previewData?.missingLabourDefinitions?.length ?? 0) > 0;
        if (hasMissing && !allLabourItemsResolved) return false;
        const hasBlockingErrors =
          previewData?.hasErrors && !allLabourItemsResolved;
        return Boolean(previewData && !hasBlockingErrors && !importing);
      }
      default:
        return false;
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      const { isDirty } = form.formState;
      if (isDirty || importPath !== null) {
        setShowCancelConfirm(true);
      } else {
        navigate(Routes.sites.manage);
      }
    } else {
      previousStep();
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      const valid = await form.trigger(['name', 'slug', 'what3words', 'notes']);
      if (!valid) return;
      nextStep();
      return;
    }
    if (activeStep === 2 && importPath === 'template') {
      await onTemplateConfirm();
      return;
    }
    nextStep();
  };

  const isLastStep = activeStep === 3;
  const canAdvance = canGoNext();
  const loading = uploading || importing;
  const sidebarOffset = isDesktop && !sidebarCollapsed ? SIDEBAR_WIDTH : 0;

  return (
    <AppBar
      position="fixed"
      sx={{
        top: 'auto',
        bottom: 0,
        left: sidebarOffset,
        width: `calc(100% - ${sidebarOffset}px)`,
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        boxShadow: theme => theme.shadows[8],
        transition: theme =>
          theme.transitions.create(['width', 'left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
      }}
    >
      <Toolbar
        sx={{ justifyContent: 'space-between', minHeight: { xs: 48, sm: 48 } }}
      >
        {/* Left: Back + step indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleBack}
            disabled={loading}
            sx={{ minWidth: isMobile ? 40 : 'auto' }}
            data-testid="stepper-back-button"
          >
            {isMobile ? '' : 'Back'}
          </Button>

          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="stepper-step-indicator"
          >
            {activeStep + 1}/4 {currentStepLabels[activeStep]}
          </Typography>
        </Box>

        {/* Right: Cancel + Primary */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Close />}
            onClick={onCancel}
            color="error"
            sx={{ minWidth: isMobile ? 40 : 'auto' }}
            data-testid="stepper-cancel-button"
          >
            {isMobile ? '' : 'Cancel'}
          </Button>

          {isLastStep ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<Check />}
              onClick={onImport}
              disabled={!canAdvance || loading}
              sx={{
                minWidth: isMobile ? 40 : 'auto',
                fontWeight: 700,
                px: 3,
              }}
              data-testid="stepper-confirm-import-button"
            >
              {isMobile ? '' : importing ? 'Creating...' : 'Create site'}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<ArrowForward />}
              onClick={handleNext}
              disabled={!canAdvance || loading}
              sx={{ minWidth: isMobile ? 40 : 'auto' }}
              data-testid="stepper-next-button"
            >
              {isMobile ? '' : 'Continue'}
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default SiteImportBottomToolbar;
